import { useState } from "react";
import PageHeader from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { BookOpen, Home, User, Search, MessageCircle, Settings, Star } from "lucide-react";

const UserManual = () => {
  const [selectedChapter, setSelectedChapter] = useState("overview");

  const chapters = [
    {
      id: "overview",
      title: "Overview",
      icon: Home,
      content: (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-foreground">ALPHA User Manual</h1>
          <p className="text-muted-foreground">
            Welcome to ALPHA - the premier social platform for the LGBTQ+ community. This comprehensive guide will help you make the most of your experience.
          </p>
          
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Profile Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Learn how to create an engaging profile, manage photos, and optimize your visibility.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Discovery & Browsing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Discover how to find and connect with like-minded individuals in your area.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Messaging & Chat
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Master the art of conversation and build meaningful connections.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Settings & Privacy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Customize your experience and protect your privacy.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },
    {
      id: "profile-management",
      title: "Profile Management",
      icon: User,
      content: (
        <div className="space-y-6 prose prose-slate dark:prose-invert max-w-none">
          <h1>Chapter 2: Profile Management</h1>
          
          <h2>2.1 Overview</h2>
          <p>Your profile is your digital representation on the app. A complete and engaging profile helps you connect with like-minded individuals and increases your chances of meaningful matches.</p>
          
          <h2>2.2 Creating Your Profile</h2>
          
          <h3>2.2.1 Profile Completion Process</h3>
          <p>After account creation, you'll be guided through the profile completion process:</p>
          <ol>
            <li><strong>Profile Photos</strong> - Upload and arrange your photos</li>
            <li><strong>Basic Information</strong> - Add personal details</li>
            <li><strong>Hierarchy Type</strong> - Select your preferences</li>
            <li><strong>Interests & Preferences</strong> - Define what you're looking for</li>
            <li><strong>Age Range & Location</strong> - Set your discovery preferences</li>
          </ol>
          
          <h3>2.2.2 Profile Completion Requirements</h3>
          <p>To activate your profile, you must complete:</p>
          <ul>
            <li>✅ Upload at least one profile photo</li>
            <li>✅ Add display name</li>
            <li>✅ Set date of birth</li>
            <li>✅ Add basic bio information</li>
            <li>✅ Set interests and preferences</li>
          </ul>
          
          <h2>2.3 Photo Management</h2>
          
          <h3>2.3.1 Adding Photos</h3>
          <ul>
            <li><strong>Maximum Photos</strong>: Up to 5 photos per profile</li>
            <li><strong>Main Photo</strong>: First photo acts as your primary profile image</li>
            <li><strong>Upload Methods</strong>: 
              <ul>
                <li>Click "Add Photo" button</li>
                <li>Drag and drop files</li>
                <li>Select from device gallery</li>
              </ul>
            </li>
          </ul>
          
          <h3>2.3.2 Photo Requirements</h3>
          <ul>
            <li><strong>Format</strong>: JPG, PNG, GIF supported</li>
            <li><strong>Quality</strong>: High-resolution images recommended</li>
            <li><strong>Content</strong>: Must comply with community guidelines</li>
            <li><strong>Main Photo Cropping</strong>: First photo automatically cropped to square format</li>
          </ul>
          
          <h2>2.4 Best Practices</h2>
          
          <h3>2.4.1 Photo Tips</h3>
          <ul>
            <li>Use recent, clear photos</li>
            <li>Show your personality</li>
            <li>Include variety (close-up, full body, activities)</li>
            <li>Good lighting improves attractiveness</li>
            <li>Authentic photos perform better</li>
          </ul>
          
          <h3>2.4.2 Bio Writing Tips</h3>
          <ul>
            <li>Be authentic and honest</li>
            <li>Mention specific interests</li>
            <li>Include what you're looking for</li>
            <li>Add humor when appropriate</li>
            <li>Keep it concise but informative</li>
          </ul>
        </div>
      )
    }
  ];

  const selectedChapterData = chapters.find(chapter => chapter.id === selectedChapter);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader 
        title="User Manual" 
        icon={BookOpen}
      />
      
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Table of Contents */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Table of Contents</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[600px]">
                  <div className="space-y-2 p-6">
                    {chapters.map((chapter) => {
                      const Icon = chapter.icon;
                      return (
                        <Button
                          key={chapter.id}
                          variant={selectedChapter === chapter.id ? "default" : "ghost"}
                          className="w-full justify-start"
                          onClick={() => setSelectedChapter(chapter.id)}
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {chapter.title}
                        </Button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
          
          {/* Content */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {selectedChapterData && (
                    <>
                      <selectedChapterData.icon className="w-5 h-5" />
                      {selectedChapterData.title}
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <Separator />
              <CardContent className="p-6">
                <ScrollArea className="h-[600px]">
                  {selectedChapterData?.content}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManual;